// Ubicación en el repo: src/pages/Agenda.jsx
// Agenda (spec-pacientes-agenda.md §2). Fiel a prototipo-actual.html:
// selector de vista lista (chips de día, renderAgenda ~862-871) / vista
// calendario (renderMonthGrid ~916-960), leyendas de color por tipo y por
// estado (~891-901), grilla horaria del día (renderDayCalendar), alertas
// de control de audífonos (renderControlAlerts ~1249-1273, solo ORL) e
// impresión de agenda del día (printDayAgenda ~1062-1096; el comprobante
// de cita individual vive en AppointmentFormModal).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  listDayAppointments,
  listMonthAppointments,
  softDeleteAppointment,
  notifyAppointmentCancellation,
  notifyAppointmentReminder,
  markWhatsappReminderOpened,
  updateAppointmentStatus,
  moveAppointmentTime,
  listPotentialConflicts,
  findOverlaps,
  isBlockType,
  apptTypesForModule,
  reminderMessage,
  STATUS_FLOW,
  STATUS_COLORS,
  STATUS_ICONS,
  TYPE_COLORS,
} from '../lib/appointments';
import CancelAppointmentModal from '../components/CancelAppointmentModal';
import SendReminderModal from '../components/SendReminderModal';
import RescheduleToast from '../components/RescheduleToast';
import { listActiveProfessionals } from '../lib/professionals';
import {
  formatPatientName,
  formatProfessionalName,
  todayISO,
  addDaysISO,
  formatDateLong,
  formatDate,
} from '../lib/format';
import {
  ROW_H,
  SLOT_MIN,
  buildDaySlots,
  minutesToTime,
  timeToMinutes,
  minutesToTop,
  blockHeight,
  computeOverlapColumns,
} from '../lib/dayGridLayout';
import { DOW_LABELS, monthLabel, shiftMonth, buildMonthCells, monthRange } from '../lib/monthGrid';
import { listControlAlerts } from '../lib/hearingaidAlerts';
import { printHTML } from '../lib/print';
import { dayAgendaHTML } from '../lib/receipts';
import { getBranchLogoUrl } from '../lib/branchLogos';
import AppointmentFormModal from '../components/AppointmentFormModal';
import Spinner from '../components/Spinner';
import { supabase } from '../lib/supabaseClient';

const DAY_NAMES = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

// Pedido de Nicolás (21-ago-2026, DECISIONS.md #78): el botón de WhatsApp
// abre `wa.me` con la sesión personal de quien hace clic (ver
// reminderMessage/handleWhatsApp más abajo) — todavía no hay un número
// genérico por sucursal, así que se oculta hasta resolver eso "de buena
// manera". Toda la lógica queda intacta (handleWhatsApp, reminderMessage,
// markWhatsappReminderOpened) — para reactivarlo, este flag vuelve a true,
// nada más.
const WHATSAPP_REMINDER_ENABLED = false;

export default function Agenda() {
  const { activeBranchId, activeModule, professional, selectableBranches } = useOutletContext();
  const navigate = useNavigate();
  const [date, setDate] = useState(todayISO());
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'month'
  const [monthCursor, setMonthCursor] = useState(() => todayISO().slice(0, 7));

  const [appointments, setAppointments] = useState([]);
  const [monthAppointments, setMonthAppointments] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [controlAlerts, setControlAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [newDefaults, setNewDefaults] = useState({ date, time: '' });

  // Cancelación con aviso por correo (DECISIONS.md, 19-ago-2026): solo se
  // usa este modal cuando hay paciente con correo registrado — si no, sigue
  // el window.confirm() simple de siempre, ver handleDelete.
  const [cancelTarget, setCancelTarget] = useState(null); // { appt } | null
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reminderTarget, setReminderTarget] = useState(null); // appt | null
  const [reminderLoading, setReminderLoading] = useState(false);
  // Aviso no bloqueante de reagendamiento (24-ago-2026, pedido de Nicolás)
  // — se activa al cambiar la hora de una cita (arrastre o edición manual)
  // cuando el paciente tiene correo registrado, ver handleDrop() y
  // handleFormSaved() más abajo. Reutiliza el flujo de "Mandar
  // recordatorio" ya existente (setReminderTarget) en vez de mandar el
  // correo directo desde el aviso — ver RescheduleToast.jsx.
  const [rescheduleNotice, setRescheduleNotice] = useState(null); // appt (con fecha/hora nueva) | null
  // Fix #9 de la auditoría del 22-ago-2026 (DECISIONS.md): sin esto, un
  // doble clic rápido en dos botones de estado distintos de la misma cita
  // mandaba dos updates en paralelo y "el último que llega gana" — el
  // estado final podía terminar siendo otro distinto del último que el
  // usuario alcanzó a ver clickeado. Set de ids de citas con un cambio de
  // estado en curso; la fila de estado se deshabilita mientras el id esté
  // acá.
  const [statusUpdating, setStatusUpdating] = useState(() => new Set());

  const gridRef = useRef(null);
  const dragOffsetRef = useRef(0);

  const activeBranch = selectableBranches.find((b) => b.id === activeBranchId) || null;
  const branchName = activeBranch?.name || '';

  const refresh = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    setError('');
    try {
      const data = await listDayAppointments({ branchId: activeBranchId, module: activeModule, date });
      setAppointments(data);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la agenda.');
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, activeModule, date]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    listActiveProfessionals().then(setProfessionals).catch(() => setProfessionals([]));
  }, []);

  const refreshMonth = useCallback(async () => {
    if (viewMode !== 'month' || !activeBranchId) return;
    const { start, end } = monthRange(monthCursor);
    try {
      const data = await listMonthAppointments({
        branchId: activeBranchId,
        module: activeModule,
        monthStart: start,
        monthEnd: end,
      });
      setMonthAppointments(data);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el mes.');
    }
  }, [viewMode, monthCursor, activeBranchId, activeModule]);

  useEffect(() => {
    refreshMonth();
  }, [refreshMonth]);

  // Sincronización en vivo (25-ago-2026, pedido de Nicolás): sin esto, una
  // cita creada por otra persona (recepción, u otro profesional) o por un
  // auto-agendamiento público solo aparecía acá después de recargar la
  // página a mano. Supabase Realtime avisa al instante cuando cambia
  // cualquier fila de `appointments` en esta sucursal (insert/update/delete
  // — la publicación se agregó en la migración 0048) y esto simplemente
  // vuelve a pedir los datos, reusando el mismo `refresh()`/`refreshMonth()`
  // de siempre en vez de intentar mezclar el payload del evento a mano.
  // RLS (`appointments_select`, `has_branch_access`) sigue aplicando: cada
  // profesional solo recibe eventos de sucursales a las que tiene acceso.
  useEffect(() => {
    if (!activeBranchId) return;
    const channel = supabase
      .channel(`appointments-branch-${activeBranchId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'appointments', filter: `branch_id=eq.${activeBranchId}` },
        () => {
          refresh();
          refreshMonth();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeBranchId, refresh, refreshMonth]);

  // Alertas de control de audífonos (spec 2.4, solo módulo ORL) — se
  // recalculan junto con la agenda del día, para que aparezcan/desaparezcan
  // apenas se marca un control como hecho desde la ficha del paciente.
  useEffect(() => {
    if (activeModule !== 'ORL' || !activeBranchId) {
      setControlAlerts([]);
      return;
    }
    listControlAlerts({ branchId: activeBranchId })
      .then(setControlAlerts)
      .catch(() => setControlAlerts([]));
  }, [activeModule, activeBranchId, appointments]);

  const { slots, startMin } = buildDaySlots(appointments);
  const overlapMap = computeOverlapColumns(appointments);

  function goToList(iso) {
    setDate(iso);
    setViewMode('list');
  }

  function openNewAt(minutes) {
    setEditTarget(null);
    setNewDefaults({ date, time: minutesToTime(minutes) });
    setFormOpen(true);
  }

  function handleGridClick(e) {
    const rect = gridRef.current.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const slotIndex = Math.max(0, Math.round(offsetY / ROW_H));
    openNewAt(startMin + slotIndex * SLOT_MIN);
  }

  async function handleStatusClick(appt, status) {
    if (statusUpdating.has(appt.id)) return; // ya hay un cambio en curso para esta cita
    setStatusUpdating((prev) => new Set(prev).add(appt.id));
    try {
      await updateAppointmentStatus(appt.id, status, professional.id);
      refresh();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar el estado.');
    } finally {
      setStatusUpdating((prev) => {
        const next = new Set(prev);
        next.delete(appt.id);
        return next;
      });
    }
  }

  async function handleDelete(appt) {
    // Con paciente vinculado y correo registrado: se pide motivo y se avisa
    // por correo al cancelar (ver CancelAppointmentModal). Sin eso (citas
    // sin paciente, bloqueos de agenda, o paciente sin correo), se mantiene
    // el confirm() simple de siempre — no hay nada que avisar.
    if (appt.patient_id && appt.patients?.email) {
      setCancelTarget(appt);
      return;
    }
    const ok = window.confirm('¿Eliminar esta cita?');
    if (!ok) return;
    try {
      await softDeleteAppointment(appt.id, professional.id);
      refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la cita.');
    }
  }

  async function handleConfirmCancel(reason, notify = true) {
    const appt = cancelTarget;
    if (!appt) return;
    setCancelLoading(true);
    try {
      await softDeleteAppointment(appt.id, professional.id, reason);
      setCancelTarget(null);
      refresh();
      // Error de agendamiento del staff (24-ago-2026, pedido de Nicolás):
      // el motivo igual queda guardado arriba para auditoría interna, pero
      // si el checkbox de CancelAppointmentModal marcó "no avisar", se
      // salta directo el correo — no tiene sentido notificarle al paciente
      // una hora que nunca debió existir.
      if (!notify) return;
      // Best-effort, después de que la cancelación ya tuvo éxito — nunca
      // debe bloquear ni revertir lo de arriba (ver notifyAppointmentCancellation).
      const branch = selectableBranches.find((b) => b.id === appt.branch_id);
      const result = await notifyAppointmentCancellation({
        appointmentId: appt.id,
        patientId: appt.patient_id,
        reason,
        date: appt.date,
        time: appt.time,
        type: appt.type,
        branchName: branch?.name,
        branchPhone: branch?.phone,
      });
      if (!result?.sent) {
        setError(
          'La cita se canceló, pero no se pudo enviar el correo de aviso al paciente — avísale manualmente.'
        );
      }
    } catch (err) {
      setError(err.message || 'No se pudo cancelar la cita.');
    } finally {
      setCancelLoading(false);
    }
  }

  function handleWhatsApp(appt) {
    const p = appt.patients;
    if (!p) return;
    const digits = (p.phone || '').replace(/\D/g, '');
    if (!digits) {
      setError('Este paciente no tiene teléfono registrado.');
      return;
    }
    const msg = encodeURIComponent(reminderMessage(appt, p, activeBranch));
    window.open(`https://wa.me/${digits}?text=${msg}`, '_blank');
    // Best-effort, no bloquea nada si falla — ver comentario de la columna
    // en la migración 0033 (solo registra que se ABRIÓ el link, no que se
    // mandó de verdad).
    markWhatsappReminderOpened(appt.id).catch(() => {});
  }

  // Abre la confirmación en la app (SendReminderModal) en vez de mandar
  // directo — el clic ahora dispara un correo REAL por Resend (ya no un
  // `mailto:` que el staff revisaba antes de mandar), así que hace falta
  // este paso para no mandar un correo real de un mal clic en un bloque de
  // 15 minutos (pedido de Nicolás, 20-ago-2026, DECISIONS.md #76).
  function handleEmailReminder(appt) {
    const p = appt.patients;
    if (!p?.email) {
      setError('Este paciente no tiene correo registrado.');
      return;
    }
    setReminderTarget(appt);
  }

  async function handleConfirmReminder() {
    const appt = reminderTarget;
    if (!appt) return;
    setReminderLoading(true);
    try {
      const result = await notifyAppointmentReminder({
        appointmentId: appt.id,
        patientId: appt.patient_id,
        date: appt.date,
        time: appt.time,
        type: appt.type,
        branchName: activeBranch?.name,
        branchAddress: activeBranch?.address,
        branchPhone: activeBranch?.phone,
        branchMapsLinkEnabled: activeBranch?.maps_link_enabled !== false,
      });
      setReminderTarget(null);
      if (result?.sent) {
        refresh();
      } else {
        setError('No se pudo mandar el recordatorio — revisa que el paciente tenga correo registrado.');
      }
    } catch (err) {
      setError(err.message || 'No se pudo mandar el recordatorio.');
    } finally {
      setReminderLoading(false);
    }
  }

  function handleDragStart(e, appt) {
    const cardRect = e.currentTarget.getBoundingClientRect();
    dragOffsetRef.current = e.clientY - cardRect.top;
    e.dataTransfer.setData('text/plain', appt.id);
    e.currentTarget.classList.add('dragging');
  }

  async function handleDrop(e) {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    const appt = appointments.find((a) => a.id === id);
    if (!appt) return;

    const rect = gridRef.current.getBoundingClientRect();
    const offsetY = e.clientY - rect.top - dragOffsetRef.current;
    const slotIndex = Math.max(0, Math.round(offsetY / ROW_H));
    const newTime = minutesToTime(startMin + slotIndex * SLOT_MIN);
    if (newTime === appt.time?.slice(0, 5)) return;

    try {
      const existing = await listPotentialConflicts({
        branchId: activeBranchId,
        module: activeModule,
        date,
        excludeId: appt.id,
      });
      const overlaps = findOverlaps(
        { time: newTime, duration: appt.duration, professionalId: appt.professional_id },
        existing
      );
      if (overlaps.length > 0) {
        const ok = window.confirm(
          `Ese horario choca con ${overlaps.length} cita(s) existente(s). ¿Mover de todas formas?`
        );
        if (!ok) return;
      }
      await moveAppointmentTime(appt.id, newTime, professional.id);
      refresh();
      // Aviso no bloqueante de reagendamiento (24-ago-2026) — solo si hay
      // paciente con correo a quien avisarle (los bloqueos de agenda no
      // tienen patient_id, quedan afuera solos).
      if (appt.patient_id && appt.patients?.email) {
        setRescheduleNotice({ ...appt, time: newTime });
      }
    } catch (err) {
      if (err.code === '23P01') {
        // Mensaje genérico (22-ago-2026, fix #2 de la auditoría) — ver
        // mismo comentario en AppointmentFormModal.jsx.
        setError('Ya existe otra cita o bloqueo activo en ese horario — no se pudo mover.');
      } else {
        setError(err.message || 'No se pudo mover la cita.');
      }
    }
  }

  function handleFormSaved(result) {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
    // Mismo aviso que en handleDrop, para cuando el reagendamiento se hizo
    // editando fecha/hora a mano desde "Editar cita" en vez de arrastrando
    // (24-ago-2026, pedido de Nicolás — ver AppointmentFormModal.jsx).
    if (result?.dateTimeChanged && result.previous?.patient_id && result.previous?.patients?.email) {
      setRescheduleNotice({
        ...result.previous,
        date: result.values.date,
        time: result.values.time,
      });
    }
  }

  async function handlePrintDayAgenda() {
    const logoUrl = await getBranchLogoUrl(activeBranch);
    const html = dayAgendaHTML({ date, branchName, module: activeModule, appointments, isBlockType, logoUrl });
    printHTML(`Agenda del dia - ${branchName} - ${date}`, html);
  }

  const dayChips = [];
  for (let i = -2; i < 10; i++) dayChips.push(addDaysISO(todayISO(), i));

  const monthCells = buildMonthCells(monthCursor);

  return (
    <div className="agenda-page">
      <div className="card-panel">
        <div className="agenda-toolbar-row">
          <div className="agenda-view-toggle">
            <div className="view-toggle">
              <button
                type="button"
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
              >
                📋 Vista lista
              </button>
              <button
                type="button"
                className={viewMode === 'month' ? 'active' : ''}
                onClick={() => {
                  setMonthCursor(date.slice(0, 7));
                  setViewMode('month');
                }}
              >
                🗓 Vista calendario
              </button>
            </div>
          </div>
          <button type="button" className="button-secondary" onClick={handlePrintDayAgenda}>
            🖨 Imprimir agenda del día
          </button>
        </div>

        {viewMode === 'list' ? (
          <div className="day-chips">
            {dayChips.map((iso) => {
              const d = new Date(iso + 'T00:00:00');
              const isToday = iso === todayISO();
              return (
                <div
                  key={iso}
                  className={`day-chip ${iso === date ? 'active' : ''}`}
                  onClick={() => setDate(iso)}
                >
                  <div className="day-chip__d">{isToday ? 'hoy' : DAY_NAMES[d.getDay()]}</div>
                  <div className="day-chip__n">{d.getDate()}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="month-grid-wrap">
            <div className="month-grid-header">
              <button type="button" className="link-button" onClick={() => setMonthCursor((m) => shiftMonth(m, -1))}>
                ←
              </button>
              <strong>{monthLabel(monthCursor)}</strong>
              <button
                type="button"
                className="button-secondary"
                onClick={() => {
                  setMonthCursor(todayISO().slice(0, 7));
                  setDate(todayISO());
                }}
              >
                Hoy
              </button>
              <button type="button" className="link-button" onClick={() => setMonthCursor((m) => shiftMonth(m, 1))}>
                →
              </button>
            </div>
            <div className="month-grid">
              {DOW_LABELS.map((d) => (
                <div key={d} className="month-grid__dow">
                  {d}
                </div>
              ))}
              {monthCells.map((d, idx) => {
                if (d === null) return <div key={idx} className="month-grid__cell month-grid__cell--empty" />;
                const iso = `${monthCursor}-${String(d).padStart(2, '0')}`;
                const dayApts = monthAppointments
                  .filter((a) => a.date === iso)
                  .sort((a, b) => a.time.localeCompare(b.time));
                const isToday = iso === todayISO();
                const isSelected = iso === date;
                const shown = dayApts.slice(0, 3);
                const moreCount = dayApts.length - shown.length;
                return (
                  <div
                    key={idx}
                    className={`month-grid__cell ${isSelected ? 'selected' : ''}`}
                    onClick={() => setDate(iso)}
                  >
                    <div className={`month-grid__daynum ${isToday ? 'today' : ''}`}>{d}</div>
                    {shown.map((a) => (
                      <div
                        key={a.id}
                        className="month-grid__chip"
                        style={{
                          borderLeftColor: TYPE_COLORS[a.type] || 'var(--primary)',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToList(iso);
                        }}
                        title={`${a.time?.slice(0, 5)} · ${isBlockType(a.type) ? a.type : formatPatientName(a.patients)}`}
                      >
                        <span className="mono">{a.time?.slice(0, 5)}</span>{' '}
                        {isBlockType(a.type) ? a.type : formatPatientName(a.patients)}
                      </div>
                    ))}
                    {moreCount > 0 && (
                      <div
                        className="month-grid__more"
                        onClick={(e) => {
                          e.stopPropagation();
                          goToList(iso);
                        }}
                      >
                        +{moreCount} más
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="card-panel agenda-detail">
        <div className="agenda-detail__header">
          <h2>
            {formatDateLong(date)} · Agenda {activeModule} · {branchName}
          </h2>
          <span className="text-muted">Clic en una hora libre para agendar · arrastra una cita para moverla</span>
        </div>

        <div className="cal-legend">
          {apptTypesForModule(activeModule).map((t) => (
            <span key={t}>
              <span className="dot-square" style={{ background: TYPE_COLORS[t] || 'var(--primary)' }} />
              {t}
            </span>
          ))}
        </div>
        <div className="cal-legend">
          <span className="cal-legend__label">Estado:</span>
          {STATUS_FLOW.map((s) => (
            <span key={s}>
              {STATUS_ICONS[s] ? (
                <span className="cal-legend__icon">{STATUS_ICONS[s]}</span>
              ) : (
                <span className="dot" style={{ background: STATUS_COLORS[s] }} />
              )}
              {s === 'No se presentó' ? 'No se presentó (NSP)' : s}
            </span>
          ))}
        </div>

        {error && (
          <p className="auth-error" onClick={() => setError('')}>
            {error} <span className="text-muted">(clic para cerrar)</span>
          </p>
        )}
        {loading && <Spinner />}
        {!loading && appointments.length === 0 && (
          <p className="text-muted">
            Sin citas este día en el módulo {activeModule}. Haz clic directamente sobre una hora en la
            grilla de abajo.
          </p>
        )}

        <div className="day-grid-wrapper">
          <div className="day-grid-times">
            {slots.map((m) => (
              <div key={m} className="day-grid-time-label" style={{ height: ROW_H }}>
                {minutesToTime(m)}
              </div>
            ))}
          </div>

          <div
            className="day-grid"
            ref={gridRef}
            onClick={handleGridClick}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            style={{ height: slots.length * ROW_H }}
          >
            {slots.map((m) => (
              <div key={m} className="day-grid-row" style={{ height: ROW_H }} />
            ))}

            {appointments.map((appt) => {
              const layout = overlapMap.get(appt.id) || { col: 0, cols: 1 };
              const widthPct = 100 / layout.cols;
              const leftPct = layout.col * widthPct;
              const block = isBlockType(appt.type);
              const status = appt.status || 'Pendiente';
              const typeColor = TYPE_COLORS[appt.type] || 'var(--primary)';

              return (
                <div
                  key={appt.id}
                  className={`appt-block ${block ? 'appt-block--bloqueo' : ''}`}
                  style={{
                    top: minutesToTop(timeToMinutes(appt.time), startMin),
                    height: blockHeight(appt.duration, block),
                    left: `${leftPct}%`,
                    width: `calc(${widthPct}% - 4px)`,
                    borderLeftColor: block ? undefined : STATUS_COLORS[status],
                    background: block ? undefined : `${STATUS_COLORS[status]}22`,
                    zIndex: 2 + layout.col,
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, appt)}
                  onDragEnd={(e) => e.currentTarget.classList.remove('dragging')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditTarget(appt);
                    setFormOpen(true);
                  }}
                  title="Clic para editar · arrastra para mover"
                >
                  <div className="appt-block__header-row">
                    <div className="appt-block__label">
                      {!block &&
                        (appt.booking_source === 'self_service' ? (
                          <span
                            className="appt-block__source-icon"
                            title="Agendada por el paciente (auto-agendamiento)"
                            aria-label="Auto-agendado"
                          >
                            🌐
                          </span>
                        ) : (
                          <span
                            className="appt-block__source-icon"
                            title={
                              appt.creator
                                ? `Agendada en sucursal por ${formatProfessionalName(appt.creator, { withRole: true })}`
                                : 'Agendada en sucursal'
                            }
                            aria-label="Agendada en sucursal"
                          >
                            🏢
                          </span>
                        ))}
                      {appt.time?.slice(0, 5)} ·{' '}
                      {block ? (
                        appt.notes || appt.type
                      ) : (
                        <>
                          {formatPatientName(appt.patients)} ·{' '}
                          {/* Cuadrado de color del tipo de atención, pegado al texto del
                              tipo (24-ago-2026, pedido de Nicolás) — antes vivía al
                              principio del bloque, justo al lado del borde de color de
                              ESTADO (11px, ver .appt-block más abajo), y quedaba muy
                              apretado ahí, fácil de confundir un color con el otro. Acá
                              queda claro que este cuadrado describe la palabra que tiene
                              al lado. */}
                          <span className="dot-square" style={{ background: typeColor }} />
                          {appt.type}
                        </>
                      )}
                      {appt.professionals ? ` · ${formatProfessionalName(appt.professionals, { withRole: true })}` : ''}
                    </div>

                    <div className="appt-block__icons">
                      {!block && (
                        <div
                          className={`appt-block__status-row ${appt.duration <= 15 ? 'appt-block__status-row--compact' : ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {STATUS_FLOW.map((st) => (
                            <button
                              key={st}
                              type="button"
                              className={status === st ? 'active' : ''}
                              style={status === st ? { background: STATUS_COLORS[st] } : undefined}
                              title={st}
                              disabled={statusUpdating.has(appt.id)}
                              onClick={() => handleStatusClick(appt, st)}
                            >
                              {STATUS_ICONS[st]}
                            </button>
                          ))}
                        </div>
                      )}
                      <div
                        className={`appt-block__actions ${appt.duration <= 15 ? 'appt-block__actions--compact' : ''}`}
                      >
                        {!block && (
                          <>
                            {WHATSAPP_REMINDER_ENABLED && (
                              <button
                                type="button"
                                title="Recordatorio por WhatsApp"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleWhatsApp(appt);
                                }}
                              >
                                💬
                              </button>
                            )}
                            <button
                              type="button"
                              title="Recordatorio por correo"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEmailReminder(appt);
                              }}
                            >
                              ✉️
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          title="Eliminar"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(appt);
                          }}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-muted agenda-hint">
          Clic en un espacio vacío para crear una cita. Clic sobre una cita para editarla. Arrastra una
          cita para moverla de horario.
        </p>
      </div>

      {activeModule === 'ORL' && controlAlerts.length > 0 && (
        <div className="card-panel control-alerts">
          <h3>Controles de audífonos próximos o vencidos</h3>
          {controlAlerts.map((a) => (
            <div key={a.id} className="apt-item">
              <span className={`badge ${a.overdue ? 'badge-danger' : 'badge-warn'}`}>
                {a.overdue ? 'Vencido' : 'Próximo'}
              </span>
              <div className="apt-info">
                <div className="name">{a.patient ? formatPatientName(a.patient) : 'Paciente'}</div>
                <div className="meta">Control de audífono · {formatDate(a.date)}</div>
              </div>
              {a.patient?.id && (
                <button
                  type="button"
                  className="button-secondary"
                  onClick={() => navigate(`/app/pacientes/${a.patient.id}`)}
                >
                  Ver
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <AppointmentFormModal
          appointment={editTarget}
          defaults={newDefaults}
          branchId={activeBranchId}
          branch={activeBranch}
          module={activeModule}
          professionals={professionals}
          actorId={professional.id}
          onClose={() => {
            setFormOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleFormSaved}
        />
      )}

      {cancelTarget && (
        <CancelAppointmentModal
          appointment={cancelTarget}
          patient={cancelTarget.patients}
          onCancel={() => setCancelTarget(null)}
          onConfirm={handleConfirmCancel}
          loading={cancelLoading}
        />
      )}

      {reminderTarget && (
        <SendReminderModal
          appointment={reminderTarget}
          patient={reminderTarget.patients}
          branch={activeBranch}
          onCancel={() => setReminderTarget(null)}
          onConfirm={handleConfirmReminder}
          loading={reminderLoading}
        />
      )}

      {rescheduleNotice && (
        <RescheduleToast
          appointment={rescheduleNotice}
          onDismiss={() => setRescheduleNotice(null)}
          onNotify={() => {
            handleEmailReminder(rescheduleNotice);
            setRescheduleNotice(null);
          }}
        />
      )}
    </div>
  );
}
